import { isMathCategory } from '@echo/core';
import { type MathEntry, useMathLibrary } from '@echo/core/react';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { BrowsePage } from '@/components/browse-page';
import { MathRow } from '@/components/library-items';
import { mathArt } from '@/lib/math-art';

export const Route = createFileRoute('/_app/library_/math/$category')({
  component: MathBrowsePage,
});

/**
 * Library › Math › a category (Figma library › browse): the category's rows from the Math
 * library, how many there are, and each row's illustration, category and levels. Choosing a
 * row opens its info.
 */
function MathBrowsePage() {
  const { category } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const math = useMathLibrary();
  const open = isMathCategory(category)
    ? math.categories.find((candidate) => candidate.key === category)
    : undefined;
  const rows = math.entries.filter((entry) => entry.category === open?.key);

  // A row opens its info first (Figma library › math info): start it or save it from there.
  const openSet = (entry: MathEntry) =>
    void navigate({
      to: '/library/math/set/$lineageUuid',
      params: { lineageUuid: entry.lineageUuid },
    });

  return (
    <BrowsePage
      onBack={() =>
        canGoBack ? router.history.back() : navigate({ to: '/library', search: { shelf: 'math' } })
      }
      ready={math.ready}
      failed={math.failed}
      onRetry={math.retry}
      title={open?.label}
      count={rows.length}
    >
      {rows.map((entry) => (
        <MathRow
          key={entry.lineageUuid}
          title={entry.title}
          byline={entry.byline}
          tags={entry.tags}
          art={{ src: mathArt(entry) }}
          onClick={() => openSet(entry)}
        />
      ))}
    </BrowsePage>
  );
}
